/**
 * The library screen's words.
 *
 * `en` is the source, and `fr` is typed as covering every one of its keys, so
 * a forgotten translation is a build error rather than an English word that
 * quietly shows up in a French interface.
 */

const en = {
  // The shelf itself: header, heading, counts, and the two empty states.
  libHome: "ReadCommons, back to the library",
  libSearch: "Search",
  libSignedInAs: "Signed in as {user}. Sign out.",
  libTitle: "Library",
  libBookOne: "{count} book",
  libBookMany: "{count} books",
  libLoading: "Loading the library",
  libNoMatch: "No book matches these filters.",
  libNoMatchHint: "Drop one of the filters below, or clear them all and start again.",
  libEmpty: "This library is empty.",
  libEmptyHint:
    "Point READCOMMONS_LIBRARY_ROOT at the folder that holds your books, then run a reindex. ReadCommons only ever reads from it.",
  libClearFilters: "Clear all filters",
  libAllOf: "That is all {count} of them.",
  libLoadFailed: "The library could not be loaded.",
  libOffline: "No network. Only the books kept on this device are shown here.",
  libOfflineEmptyTitle: "No book has been kept for offline reading yet.",
  libOfflineEmptyHint:
    "Open a book while online, then use its offline button to keep it here. It will then open with no network at all.",

  // The Continue row.
  continueTitle: "Continue",
  continueResume: "Resume {title}",

  // One card: its marks, its progress, and the time it has left.
  bookByAuthor: "{title}, by {author}",
  bookLangTitle: "Language: {lang}",
  bookMarkSync: "Audio synchronised with the text",
  bookMarkAudio: "Audiobook",
  bookMarkDoc: "Original document",
  bookPercentRead: "{n} percent read",
  bookUnderMinute: "under a minute",
  bookMinutes: "{n} min",
  bookHours: "{h} h",
  bookHoursMinutes: "{h} h {m} min",
  bookLeft: "{time} left",
  bookPercent: "{n} %",
  bookFinished: "Read",
  bookFinishedOn: "Read, finished {date}",
  bookNotStarted: "Not started",

  // The two shelf layouts, and the control that swaps them.
  viewLabel: "Layout",
  viewGrid: "Covers",
  viewList: "List",

  // The filters, in the desktop bar and in the phone sheet.
  filterTitle: "Filters",
  filterStatus: "Status",
  filterLanguage: "Language",
  filterShelf: "Shelf",
  filterGenre: "Genre",
  filterSort: "Sort",
  filterAll: "All",
  filterReading: "Reading",
  filterFinished: "Finished",
  filterUnread: "Unread",
  sortAuthor: "Author",
  sortTitle: "Title",
  sortRecent: "Recent",
  sortProgress: "Progress",
  filterRemove: "Remove filter {text}",
  filterClearAll: "Clear all",
  filterActiveOne: "{count} active",
  filterActiveMany: "{count} active",
  filterClose: "Close filters",
  filterShowOne: "Show {count} book",
  filterShowMany: "Show {count} books",

  // Full text search, as an overlay.
  searchLibrary: "Search the library",
  searchPlaceholder: "Search titles, authors, and the text of every book",
  searchBusy: "Searching",
  searchClose: "Close search",
  searchHint: "Type at least two letters. Arrows to move, Enter to open, Escape to close.",
  searchNothing: 'Nothing matches "{term}".',
  searchBooks: "Books ({n})",
  searchPassages: "Passages ({n})",
  searchChapter: "chapter {n}",
  // Bringing a book in from a catalogue. The third section of the search panel,
  // and the strip at the top of the shelf that says how far it got.
  addSection: "Add to your library ({n})",
  addBusy: "Asking the catalogues",
  addButton: "Add",
  addAdding: "Adding",
  addOwned: "Already yours",
  addQueued: "On its way",
  addScan: "Scanned pages",
  addNoScan: "Text only",
  addSourceArchive: "Internet Archive",
  addSourceGutenberg: "Project Gutenberg",
  addHint: "Not on your shelf. Adding one takes a few hours: the text is translated, read aloud, and lined up with the page.",
  addNoWorker: "Nothing is draining this queue, so a book added now will sit here. Start readcommons-acquire.service.",
  prepTitle: "Being prepared",
  prepStep: "Step {n} of {total}",
  prepCancel: "Remove from the queue",
  prepFailed: "Stopped: {message}",
  prepRetry: "Try again",
  prepReady: "Ready to read",
  stepFetch: "Downloading the text",
  stepInstall: "Filing it in the library",
  stepTranslate: "Translating into French",
  stepVoice: "Preparing the voice",
  stepIndex: "Indexing",
  stepClean: "Cleaning the text",
  stepNarrate: "Narrating and lining up the words",
  stepCover: "Finding a cover",
} as const;

const fr: Record<keyof typeof en, string> = {
  libHome: "ReadCommons, retour à la bibliothèque",
  libSearch: "Rechercher",
  libSignedInAs: "Connecté : {user}. Se déconnecter.",
  libTitle: "Bibliothèque",
  libBookOne: "{count} livre",
  libBookMany: "{count} livres",
  libLoading: "Chargement de la bibliothèque",
  libNoMatch: "Aucun livre ne correspond à ces filtres.",
  libNoMatchHint: "Retirez un des filtres ci-dessous, ou effacez-les tous pour repartir de zéro.",
  libEmpty: "Cette bibliothèque est vide.",
  libEmptyHint:
    "Faites pointer READCOMMONS_LIBRARY_ROOT vers le dossier qui contient vos livres, puis relancez l'indexation. ReadCommons ne fait qu'y lire.",
  libClearFilters: "Effacer tous les filtres",
  libAllOf: "Et c'est tout : {count} livres.",
  libLoadFailed: "La bibliothèque n'a pas pu être chargée.",
  libOffline: "Pas de réseau. Seuls les livres gardés sur cet appareil sont ici.",
  libOfflineEmptyTitle: "Aucun livre n'a encore été gardé pour une lecture hors ligne.",
  libOfflineEmptyHint:
    "Ouvrez un livre en étant connecté, puis utilisez son bouton hors ligne pour le garder ici. Il s'ouvrira ensuite sans aucun réseau.",

  continueTitle: "Reprendre",
  continueResume: "Reprendre {title}",

  bookByAuthor: "{title}, par {author}",
  bookLangTitle: "Langue : {lang}",
  bookMarkSync: "Audio synchronisé avec le texte",
  bookMarkAudio: "Livre audio",
  bookMarkDoc: "Document original",
  bookPercentRead: "{n} % lu",
  bookUnderMinute: "moins d'une minute",
  bookMinutes: "{n} min",
  bookHours: "{h} h",
  bookHoursMinutes: "{h} h {m}",
  bookLeft: "il reste {time}",
  bookPercent: "{n} %",
  bookFinished: "Lu",
  bookFinishedOn: "Lu, terminé le {date}",
  bookNotStarted: "Pas commencé",

  viewLabel: "Affichage",
  viewGrid: "Couvertures",
  viewList: "Liste",

  filterTitle: "Filtres",
  filterStatus: "Statut",
  filterLanguage: "Langue",
  filterShelf: "Rayon",
  filterGenre: "Genre",
  filterSort: "Tri",
  filterAll: "Tous",
  filterReading: "En cours",
  filterFinished: "Terminés",
  filterUnread: "Non lus",
  sortAuthor: "Auteur",
  sortTitle: "Titre",
  sortRecent: "Récent",
  sortProgress: "Avancement",
  filterRemove: "Retirer le filtre {text}",
  filterClearAll: "Tout effacer",
  filterActiveOne: "{count} filtre actif",
  filterActiveMany: "{count} filtres actifs",
  filterClose: "Fermer les filtres",
  filterShowOne: "Voir le livre",
  filterShowMany: "Voir les {count} livres",

  searchLibrary: "Rechercher dans la bibliothèque",
  searchPlaceholder: "Un titre, un auteur, ou le texte des livres",
  searchBusy: "Recherche en cours",
  searchClose: "Fermer la recherche",
  searchHint: "Tapez au moins deux lettres. Flèches pour parcourir, Entrée pour ouvrir, Échap pour fermer.",
  searchNothing: "Rien ne correspond à « {term} ».",
  searchBooks: "Livres ({n})",
  searchPassages: "Passages ({n})",
  searchChapter: "chapitre {n}",
  addSection: "Ajouter à votre bibliothèque ({n})",
  addBusy: "Interrogation des catalogues",
  addButton: "Ajouter",
  addAdding: "Ajout",
  addOwned: "Déjà chez vous",
  addQueued: "En route",
  addScan: "Pages scannées",
  addNoScan: "Texte seul",
  addSourceArchive: "Internet Archive",
  addSourceGutenberg: "Projet Gutenberg",
  addHint: "Absent de votre étagère. L'ajout prend quelques heures : le texte est traduit, lu à voix haute, puis calé sur la page.",
  addNoWorker: "Aucun service ne vide cette file : un livre ajouté maintenant y resterait. Démarrez readcommons-acquire.service.",
  prepTitle: "En préparation",
  prepStep: "Étape {n} sur {total}",
  prepCancel: "Retirer de la file",
  prepFailed: "Arrêté : {message}",
  prepRetry: "Réessayer",
  prepReady: "Prêt à lire",
  stepFetch: "Téléchargement du texte",
  stepInstall: "Installation dans la bibliothèque",
  stepTranslate: "Traduction en français",
  stepVoice: "Préparation de la voix",
  stepIndex: "Indexation",
  stepClean: "Nettoyage du texte",
  stepNarrate: "Narration et calage des mots",
  stepCover: "Recherche de la couverture",
};

export const library = { en, fr };
